import {useCallback, useMemo, useState} from 'react';
import {Eye, EyeOff, RotateCcw, ShieldCheck, Shuffle} from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import {type Item} from '@/api/items';
import {
  INDETERMINATE_REASONS,
  type IndeterminateReason,
  type ModerationTask
} from '@/api/moderationQueue';
import {MAX_REASON_LENGTH} from '@/components/queues/QueueDecisionPanel';
import {ItemsCollection} from '@/components/items/ItemsCollection';
import {ItemsBulkBar} from '@/components/items/ItemsToolbar';
import {getDeclaredLanguageSubtag} from '@/lib/itemMetadata';
import {toItem} from '@/lib/queueItem';
import {languageLabel} from '@/lib/queueFilters';
import {itemIdsForClipboard} from '@/lib/itemIds';

type Props = {
  readonly tasks: readonly ModerationTask[];
  readonly checked: ReadonlySet<string>;
  // Tasks whose original the analyst opened on the platform.
  readonly opened: ReadonlySet<string>;
  readonly activeId: string | null;
  readonly busy: boolean;
  readonly onToggle: (id: string) => void;
  readonly onSelectAll: () => void;
  readonly onClear: () => void;
  readonly onOpen: (id: string) => void;
  readonly onClassify: (task: ModerationTask) => void;
  readonly onDismiss: (task: ModerationTask) => void;
  readonly onIndeterminate: (task: ModerationTask, reason: IndeterminateReason) => void;
  readonly onSkip: (task: ModerationTask) => void;
  readonly onReclassify: (task: ModerationTask) => void;
  readonly onDismissChecked: () => void;
  readonly onIndeterminateChecked: (reason: IndeterminateReason) => void;
  readonly onSkipChecked: () => void;
  readonly onClassifyChecked: () => void;
  // Other policy violations a selection can be sent to.
  readonly targets: readonly string[];
  readonly onReclassifyChecked: (target: string, reason: string) => void;
};

// The tasks of a batch lease in the shared item grid, selected and classified the way the
// investigation views do. A card opens beside the grid for its own review; a selection can be
// classified, marked not violative, skipped or reclassified together, which is still one decision
// per task.
export function QueueBatchView({
  tasks,
  checked,
  opened,
  activeId,
  busy,
  onToggle,
  onSelectAll,
  onClear,
  onOpen,
  onClassify,
  onDismiss,
  onIndeterminate,
  onSkip,
  onReclassify,
  onDismissChecked,
  onIndeterminateChecked,
  onSkipChecked,
  onClassifyChecked,
  targets,
  onReclassifyChecked
}: Props) {
  const [reclassifying, setReclassifying] = useState(false);
  // The task a can't-review reason is being chosen for, or every checked task.
  const [indeterminateFor, setIndeterminateFor] = useState<ModerationTask | 'checked' | null>(null);
  const [indeterminateReason, setIndeterminateReason] = useState<IndeterminateReason>('removed');
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const items = useMemo(() => tasks.map((task) => toItem(task)), [tasks]);
  const taskByItemId = useMemo(() => new Map(tasks.map((task) => [task.item_id, task])), [tasks]);
  const selectedItemIds = useMemo(
    () => new Set(tasks.filter((task) => checked.has(task.id)).map((task) => task.item_id)),
    [tasks, checked]
  );

  const withTask = useCallback(
    (run: (task: ModerationTask) => void) => (item: Item) => {
      const task = taskByItemId.get(item.id);
      if (task) {
        run(task);
      }
    },
    [taskByItemId]
  );

  const extraActions = useMemo(
    () => [
      {
        key: 'not-violative',
        label: 'Not violative',
        icon: <ShieldCheck />,
        disabled: busy,
        onSelect: withTask(onDismiss)
      },
      {
        key: 'cant-review',
        label: 'Can’t review',
        icon: <EyeOff />,
        disabled: busy,
        onSelect: withTask((task) => {
          setIndeterminateReason('removed');
          setIndeterminateFor(task);
        })
      },
      {
        key: 'skip',
        label: 'Skip',
        icon: <RotateCcw />,
        disabled: busy,
        onSelect: withTask(onSkip)
      },
      {
        key: 'reclassify',
        label: 'Reclassify',
        icon: <Shuffle />,
        disabled: busy,
        onSelect: withTask(onReclassify)
      }
    ],
    [busy, withTask, onDismiss, onSkip, onReclassify]
  );

  // Stable, so the memoised cards don't all re-render with each render of the batch.
  const handleToggleCheck = useCallback(
    (itemId: string) => {
      const task = taskByItemId.get(itemId);
      if (task) {
        onToggle(task.id);
      }
    },
    [taskByItemId, onToggle]
  );
  const handleClassify = useMemo(() => withTask(onClassify), [withTask, onClassify]);
  const handleOpen = useMemo(
    () =>
      withTask((task) => {
        onOpen(task.id);
      }),
    [withTask, onOpen]
  );

  const renderFooter = useCallback(
    (item: Item) => {
      const task = taskByItemId.get(item.id);
      if (!task) {
        return null;
      }

      const wasOpened = opened.has(task.id);
      return (
        <div className="mt-2 flex flex-col gap-1.5">
          <div className="flex flex-wrap gap-1">
            <Badge variant="secondary">{task.suggested_risk ?? 'Unclassified'}</Badge>
            <Badge variant="secondary">
              {languageLabel(getDeclaredLanguageSubtag(task.metadata))}
            </Badge>
            {activeId === task.id ? <Badge>Reviewing</Badge> : null}
          </div>
          <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
            <span>{Math.round(task.confidence * 100)}% confidence</span>
            <span
              className={`inline-flex shrink-0 items-center gap-1 ${wasOpened ? 'text-foreground' : ''}`}>
              {wasOpened ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
              {wasOpened ? 'Opened' : 'Not opened'}
            </span>
          </div>
        </div>
      );
    },
    [taskByItemId, opened, activeId]
  );

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <p className="text-muted-foreground mb-3 text-xs font-medium">
          {tasks.length} in your batch
        </p>
        <ItemsCollection
          items={items}
          view="grid"
          selectedItemIds={selectedItemIds}
          extraActions={extraActions}
          renderFooter={renderFooter}
          onToggleCheck={handleToggleCheck}
          onClassify={handleClassify}
          onOpen={handleOpen}
        />
      </div>

      <ItemsBulkBar
        selectedCount={checked.size}
        totalCount={tasks.length}
        copyIds={() => itemIdsForClipboard(items.filter((item) => selectedItemIds.has(item.id)))}
        onSelectAll={onSelectAll}
        onClassify={() => {
          if (!busy) {
            onClassifyChecked();
          }
        }}
        onClear={onClear}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={onDismissChecked}>
          <ShieldCheck />
          Not violative
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => {
            setIndeterminateReason('removed');
            setIndeterminateFor('checked');
          }}>
          <EyeOff />
          Can’t review
        </Button>
        <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={onSkipChecked}>
          <RotateCcw />
          Skip
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy || targets.length === 0}
          onClick={() => {
            setTarget(targets[0] ?? '');
            setReason('');
            setReclassifying(true);
          }}>
          <Shuffle />
          Reclassify
        </Button>
      </ItemsBulkBar>

      <Dialog
        open={indeterminateFor !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIndeterminateFor(null);
          }
        }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Can’t review{' '}
              {indeterminateFor === 'checked' ? `${checked.size} selected` : 'this item'}
            </DialogTitle>
            <DialogDescription>
              Closes the task without a decision. The risk is recorded as unknown, not as no risk.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="queue-batch-indeterminate-reason">Why?</FieldLabel>
            <NativeSelect
              id="queue-batch-indeterminate-reason"
              value={indeterminateReason}
              onChange={(event) => {
                setIndeterminateReason(event.target.value as IndeterminateReason);
              }}>
              {INDETERMINATE_REASONS.map((option) => (
                <NativeSelectOption key={option.value} value={option.value}>
                  {option.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIndeterminateFor(null);
              }}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => {
                const chosen = indeterminateFor;
                setIndeterminateFor(null);
                if (chosen === 'checked') {
                  onIndeterminateChecked(indeterminateReason);
                } else if (chosen) {
                  onIndeterminate(chosen, indeterminateReason);
                }
              }}>
              Close as can’t review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reclassifying} onOpenChange={setReclassifying}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reclassify {checked.size} selected</DialogTitle>
            <DialogDescription>
              Closes each task as misclassified by the engine and sends it to another
              vertical&apos;s queue.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="queue-bulk-reclassify-target">Move to violation</FieldLabel>
            <NativeSelect
              id="queue-bulk-reclassify-target"
              value={target}
              onChange={(event) => {
                setTarget(event.target.value);
              }}>
              {targets.map((name) => (
                <NativeSelectOption key={name} value={name}>
                  {name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor="queue-bulk-reclassify-reason">Why is the engine wrong?</FieldLabel>
            <Textarea
              id="queue-bulk-reclassify-reason"
              rows={3}
              maxLength={MAX_REASON_LENGTH}
              value={reason}
              placeholder="What does this content actually show?"
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <FieldDescription>Optional. The analyst who reviews it next sees it.</FieldDescription>
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setReclassifying(false);
              }}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy || target === ''}
              onClick={() => {
                setReclassifying(false);
                onReclassifyChecked(target, reason.trim());
              }}>
              Send to {target} queue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
